/**
 * Scheduled Revolut payments (Fan, 2026-10-27, ISS-ac0eeeeb).
 *
 * Recurring payments the app makes itself from Revolut on the due date
 * (rent etc.), including temporary amounts that revert by themselves. They
 * are set up through the assistant (Confirm card); this panel shows what
 * will be paid next, lets an approver approve, pause, resume or cancel, and
 * shows each payment's Revolut state and whether the bank line arrived.
 */
import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { revolutPaymentsAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

const SCHEDULE_STATUS = {
  active: { label: 'Active', variant: 'default' },
  pending_approval: { label: 'Waiting for approval', variant: 'secondary' },
  paused: { label: 'Paused', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'outline' },
};

const PAYMENT_STATE = {
  paying: 'Sending',
  created: 'Sent',
  pending: 'Sent, pending',
  completed: 'Completed',
  declined: 'Declined',
  failed: 'Failed',
  reverted: 'Reverted',
  unknown: 'Unknown, check',
  missed: 'Missed',
};
const BAD = new Set(['declined', 'failed', 'reverted', 'unknown', 'missed']);

const money = (v, ccy) => formatCurrency(Number(v), ccy);

export function RevolutScheduledPayments() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        revolutPaymentsAPI.getSchedules(),
        revolutPaymentsAPI.getStatus(),
      ]);
      setData(s.data);
      setStatus(st.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { Promise.resolve().then(() => load()); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const canApprove = !!status?.can_approve;
  const schedules = data?.schedules || [];

  return (
    <Card className="mt-4" data-testid="revolut-scheduled-payments">
      <CardHeader>
        <CardTitle className="text-base">Scheduled payments</CardTitle>
        <CardDescription>
          Paid by the app from Revolut on the due date. Set them up or change them by asking the assistant.
          Cancel the matching standing order in Revolut, or the payee is paid twice.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : schedules.length === 0 ? (
          <p className="text-sm text-muted-foreground">No scheduled payments yet.</p>
        ) : (
          <div className="space-y-4">
            {schedules.map((s) => {
              const st = SCHEDULE_STATUS[s.status] || { label: s.status, variant: 'outline' };
              const conn = (status?.connections || []).find((c) => c.id === s.connection_id);
              return (
                <div key={s.id} className="border rounded-md p-3" data-testid={`schedule-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{s.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {money(s.amount, s.currency)} to {s.counterparty_name} on day {s.day_of_month}
                        {' '}from Revolut {s.connection_id}, ref {s.reference}
                        {s.end_month ? `, last payment ${s.end_month}` : ''}
                      </p>
                      {conn && !conn.payments_enabled && s.status === 'active' && (
                        <p className="text-xs text-destructive mt-1">
                          Revolut {s.connection_id} is connected for reading only: enable payments above or nothing is paid.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={st.variant}>{st.label}</Badge>
                      {canApprove && s.status === 'pending_approval' && (
                        <Button size="sm" disabled={busy === s.id}
                          onClick={() => act(s.id, () => revolutPaymentsAPI.scheduleAction(s.id, 'approve'), 'Approved')}>
                          Approve
                        </Button>
                      )}
                      {s.status === 'active' && (
                        <Button size="sm" variant="outline" disabled={busy === s.id}
                          onClick={() => act(s.id, () => revolutPaymentsAPI.scheduleAction(s.id, 'pause'), 'Paused')}>
                          Pause
                        </Button>
                      )}
                      {canApprove && s.status === 'paused' && (
                        <Button size="sm" variant="outline" disabled={busy === s.id}
                          onClick={() => act(s.id, () => revolutPaymentsAPI.scheduleAction(s.id, 'resume'), 'Resumed')}>
                          Resume
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" disabled={busy === s.id}
                        onClick={() => {
                          if (window.confirm(`Stop all future payments for ${s.label}?`)) {
                            act(s.id, () => revolutPaymentsAPI.scheduleAction(s.id, 'cancel'), 'Cancelled');
                          }
                        }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {(s.upcoming || []).map((u) => (
                      <span key={u.month}
                        className={`px-2 py-1 rounded border ${u.temporary ? 'border-amber-400 bg-amber-50' : ''}`}
                        title={u.temporary ? 'Temporary amount' : 'Usual amount'}>
                        {u.due_date}: {money(u.amount, s.currency)}
                      </span>
                    ))}
                  </div>
                  {(s.recent_payments || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {s.recent_payments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between text-xs">
                          <span>
                            {p.due_date} {money(p.amount, p.currency)}{' '}
                            <span className={BAD.has(p.state) ? 'text-destructive' : 'text-muted-foreground'}>
                              {PAYMENT_STATE[p.state] || p.state}
                            </span>
                            {p.verified ? ' · in bank feed' : ''}
                          </span>
                          {canApprove && ['failed', 'unknown', 'missed'].includes(p.state) && (
                            <Button size="sm" variant="outline" disabled={busy === p.id}
                              onClick={() => act(p.id, () => revolutPaymentsAPI.retryPayment(p.id), 'Sent again')}>
                              Retry
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RevolutScheduledPayments;
